import React, { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmptyElement } from '@ublend-npm/aula-editor-utils';
import { analyticsTrackEvent } from '../../../../../core/utils/analytics';
import {
  POST_EDITOR_START,
  COMMENT_EDITOR_START,
  INBOX_EDITOR_START,
  MATERIAL_EDITOR_FIRST_EDIT,
} from '../../../../../core/constants/analytics';

const editorTypeMap = {
  comment: COMMENT_EDITOR_START,
  createpost: POST_EDITOR_START,
  post: POST_EDITOR_START,
  inbox: INBOX_EDITOR_START,
  material: MATERIAL_EDITOR_FIRST_EDIT,
};

const editorTypeToAnalyticsEventType = (type) => editorTypeMap[type] || type;

const withAnalytics = (AulaEditor) => {
  const AulaEditorWithAnalytics = forwardRef(
    ({ onChange, correlationId, ...props }, ref) => {
      const trackedStart = useRef(false);
      const trackEvent = () => {
        const eventName = editorTypeToAnalyticsEventType(props.type);
        const { spaceId, itemType, itemId, isAdmin, isRbacEnabled } =
          props.config;
        const isV3Event = [
          POST_EDITOR_START,
          MATERIAL_EDITOR_FIRST_EDIT,
          COMMENT_EDITOR_START,
        ].includes(eventName);

        if (eventName) {
          analyticsTrackEvent(eventName, {
            // Note: messages are space agnostic, so no need to send the spaceId as a field
            ...(eventName !== INBOX_EDITOR_START && {
              activeClassroom: spaceId,
              spaceId,
            }),
            itemType,
            ...(eventName === MATERIAL_EDITOR_FIRST_EDIT &&
              typeof isRbacEnabled === 'boolean' && { isRbacEnabled }),
            ...(eventName === COMMENT_EDITOR_START && {
              isAdmin,
              isRbacEnabled,
            }),
            ...(isV3Event && {
              correlationId,
              itemId,
            }),
          });
        }
      };

      const handleOnChange = (nextEditorState, conversationId) => {
        const isEditorEmpty = nextEditorState.value.every((node) =>
          isEmptyElement(node)
        );
        if (trackedStart.current && isEditorEmpty) {
          trackedStart.current = false;
        }

        if (!trackedStart.current && !isEditorEmpty) {
          trackedStart.current = true;

          trackEvent();
        }

        if (onChange) {
          onChange(nextEditorState, conversationId);
        }
      };

      return (
        <AulaEditor
          ref={ref}
          onChange={handleOnChange}
          correlationId={correlationId}
          {...props}
        />
      );
    }
  );

  AulaEditorWithAnalytics.displayName = 'AulaEditorWithAnalytics';

  AulaEditorWithAnalytics.propTypes = {
    type: PropTypes.oneOf([
      'comment',
      'createpost',
      'post',
      'inbox',
      'material',
      'assignmentExtensions',
    ]).isRequired,
    config: PropTypes.shape({
      apiUrl: PropTypes.string,
      institution: PropTypes.string,
      filesUrl: PropTypes.string,
      uploadRetries: PropTypes.number,
      accessToken: PropTypes.string,
      userId: PropTypes.string,
      spaceId: PropTypes.string,
      assignmentId: PropTypes.string,
      conversationId: PropTypes.string,
      postId: PropTypes.string,
      materialId: PropTypes.string,
      isEducator: PropTypes.bool,
      itemType: PropTypes.string,
      itemId: PropTypes.string,
      isAdmin: PropTypes.bool,
      isRbacEnabled: PropTypes.bool,
    }),
    onChange: PropTypes.func.isRequired,
    correlationId: PropTypes.string,
  };

  AulaEditorWithAnalytics.defaultProps = {
    config: {},
    correlationId: undefined,
  };

  return AulaEditorWithAnalytics;
};

export default withAnalytics;
